import React, { useMemo } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { cartesian, colorFor, doughnut, humanize } from './charts/chartTheme';
import { EmptyState } from './ui/States';

/* ==========================================================================
   Shared pieces
   ========================================================================== */

const Legend = ({ items, total }) => (
    <ul className="w-full max-w-[300px] space-y-1.5">
        {items.map((item) => {
            const pct = total ? Math.round((item.value / total) * 100) : 0;
            return (
                <li key={item.label} className="flex items-center gap-2.5 text-base">
                    <span
                        aria-hidden="true"
                        className="w-2 h-2 rounded-[2px] shrink-0"
                        style={{ backgroundColor: item.color }}
                    />
                    <span className="text-ink-600 truncate flex-1 min-w-0">{item.label}</span>
                    <span className="text-ink-900 font-semibold tabular">{item.value}</span>
                    <span className="text-ink-400 tabular w-9 text-right">{pct}%</span>
                </li>
            );
        })}
    </ul>
);

/**
 * Donut + breakdown. When there is a single category a donut says nothing, so
 * the same data is shown as a labelled distribution instead.
 */
const Distribution = ({ items, height = 156, centerLabel = 'Total' }) => {
    const total = items.reduce((sum, i) => sum + i.value, 0);

    if (!total) {
        return (
            <EmptyState
                compact
                title="No data in this period"
                description="Values will appear once records are recorded for the selected range."
            />
        );
    }

    if (items.length === 1) {
        return (
            <div className="py-2">
                <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-[26px] leading-none font-semibold text-ink-900 tabular">
                        {total}
                    </span>
                    <span className="text-base text-ink-500">
                        all {items[0].label.toLowerCase()}
                    </span>
                </div>
                <div className="progress-track" style={{ height: 8 }}>
                    <div
                        className="progress-bar"
                        style={{ backgroundColor: items[0].color }}
                    />
                </div>
                <p className="text-label text-ink-500 mt-2.5">
                    Every record currently falls into one category — no distribution to compare yet.
                </p>
            </div>
        );
    }

    const data = {
        labels: items.map((i) => i.label),
        datasets: [
            {
                data: items.map((i) => i.value),
                backgroundColor: items.map((i) => i.color),
                borderWidth: 0,
                hoverOffset: 4,
            },
        ],
    };

    return (
        <div className="flex flex-col sm:flex-row items-center gap-5">
            <div className="relative shrink-0" style={{ width: height, height }}>
                <Doughnut data={data} options={doughnut()} />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-lg font-semibold text-ink-900 tabular leading-none">{total}</span>
                    <span className="text-micro text-ink-500 mt-1">{centerLabel}</span>
                </div>
            </div>
            <div className="flex-1 w-full min-w-0">
                <Legend items={items} total={total} />
            </div>
        </div>
    );
};

/* ==========================================================================
   Charts
   ========================================================================== */

export const PriorityChart = ({ data, height = 156 }) => {
    const items = useMemo(
        () =>
            (data || []).map((d, i) => ({
                label: humanize(d.priority),
                value: Number(d.count) || 0,
                color: colorFor(d.priority, i),
            })),
        [data]
    );

    if (!items.length) {
        return (
            <EmptyState
                compact
                title="No active tasks"
                description="Priority distribution appears once tasks are open."
            />
        );
    }

    return <Distribution items={items} height={height} centerLabel="Tasks" />;
};

export const ProjectStatusChart = ({ data, height = 156 }) => {
    const items = useMemo(
        () =>
            (data || []).map((d, i) => ({
                label: humanize(d.status),
                value: Number(d.count) || 0,
                color: colorFor(d.status, i),
            })),
        [data]
    );

    if (!items.length) {
        return (
            <EmptyState
                compact
                title="No projects yet"
                description="Status breakdown appears once projects are created."
            />
        );
    }

    return <Distribution items={items} height={height} centerLabel="Projects" />;
};

/** Horizontal stacked bars — names stay readable however long they are. */
export const TeamPerformanceChart = ({ data }) => {
    const rows = useMemo(() => (data || []).slice(0, 8), [data]);

    if (!rows.length) {
        return (
            <EmptyState
                compact
                title="No team activity"
                description="Completion figures appear once tasks are assigned."
            />
        );
    }

    const chartData = {
        labels: rows.map((r) => r.user_name),
        datasets: [
            {
                label: 'Completed',
                data: rows.map((r) => Number(r.completed_tasks) || 0),
                backgroundColor: '#0f9d76',
                borderRadius: 2,
                barThickness: 12,
            },
            {
                label: 'In progress',
                data: rows.map((r) => Number(r.in_progress_tasks) || 0),
                backgroundColor: '#2589c9',
                borderRadius: 2,
                barThickness: 12,
            },
            {
                label: 'Overdue',
                data: rows.map((r) => Number(r.overdue_tasks) || 0),
                backgroundColor: '#dc4c3f',
                borderRadius: 2,
                barThickness: 12,
            },
        ],
    };

    const options = {
        ...cartesian({ horizontal: true, stacked: true }),
        indexAxis: 'y',
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-4 mb-3">
                {[
                    ['Completed', '#0f9d76'],
                    ['In progress', '#2589c9'],
                    ['Overdue', '#dc4c3f'],
                ].map(([label, color]) => (
                    <span key={label} className="flex items-center gap-1.5 text-label text-ink-600">
                        <span
                            aria-hidden="true"
                            className="w-2 h-2 rounded-[2px]"
                            style={{ backgroundColor: color }}
                        />
                        {label}
                    </span>
                ))}
            </div>
            <div className="flex-1 min-h-[200px]">
                <Bar data={chartData} options={options} />
            </div>
        </div>
    );
};

/** Horizontal bars — department names compare far better on the y-axis. */
export const DepartmentWorkloadChart = ({ data }) => {
    const rows = useMemo(
        () =>
            (data || [])
                .map((d) => ({ label: d.department || 'Unassigned', value: Number(d.count) || 0 }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 8),
        [data]
    );

    if (!rows.length) {
        return (
            <EmptyState
                compact
                title="No departmental workload"
                description="Active tasks grouped by department will show here."
            />
        );
    }

    const chartData = {
        labels: rows.map((r) => r.label),
        datasets: [
            {
                label: 'Active tasks',
                data: rows.map((r) => r.value),
                backgroundColor: '#0a2a66',
                borderRadius: 2,
                barThickness: 14,
            },
        ],
    };

    return (
        <div className="h-full min-h-[200px]">
            <Bar data={chartData} options={{ ...cartesian({ horizontal: true }), indexAxis: 'y' }} />
        </div>
    );
};

/** Generic vertical bar used by reports/Excom surfaces. */
export const SimpleBarChart = ({ labels = [], values = [], color = '#2589c9', label = 'Value' }) => {
    if (!labels.length) {
        return <EmptyState compact title="No data available" />;
    }
    return (
        <div className="h-full min-h-[200px]">
            <Bar
                data={{
                    labels,
                    datasets: [{ label, data: values, backgroundColor: color, borderRadius: 2, maxBarThickness: 34 }],
                }}
                options={cartesian()}
            />
        </div>
    );
};

export { Distribution };
